import { Formik, Form } from 'formik';
import { Box, Card, Text, Paragraph, Container } from '@qga/roo-ui/components';

import { ContentHeading } from 'components/Typography';
import { DatePickerInput, Field, PhoneInput } from 'components/Form';
import Row from 'components/Row';
import { InPathFlightBookingAlert } from 'components/InPathFlightBookingAlert';
import TwoColumnWithBookingSummary from 'components/Layout/TwoColumnWithBookingSummary';
import { ChangeDatesRequestDisclaimer } from 'components/Disclaimers';
import useStayDateRange from 'hooks/useStayDateRange';
import useScrollTopOnMount from 'hooks/useScrollTopOnMount';
import * as utils from 'lib/utils';

import { schema } from './schema';
import ButtonGroup from 'components/ButtonGroup';
import { useChangeDatesRequestFlow } from '../ChangeDatesRequestFlowProvider';

const RequestForm = (props) => {
  const { initialValues, onSubmitDraft, goToBookingDetails, originalValues, booking } = useChangeDatesRequestFlow();

  useScrollTopOnMount();

  return (
    <>
      <Container pt={12} pb={0}>
        <InPathFlightBookingAlert />
      </Container>
      <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmitDraft} validateOnMount>
        {(formik) => (
          <InnerForm {...formik} onClickBack={goToBookingDetails} originalValues={originalValues} booking={booking} {...props} />
        )}
      </Formik>
    </>
  );
};

const InnerForm = ({ values, onClickBack, handleSubmit, originalValues, isValid, booking }) => {
  const { minCheckInDate, minCheckOutDate, maxCheckOutDate } = useStayDateRange({ checkIn: values.checkIn });
  const doDatesDifferFromOriginal = utils.haveDatesChanged(originalValues, values);
  const propertyName = booking?.reservation?.property?.name;
  return (
    <Form>
      <TwoColumnWithBookingSummary heading="Request date change" showSummaryOnMobile>
        <Paragraph fontSize="lg" pb={[7, 8]}>
          Please enter your new stay dates and we’ll contact {propertyName ? `${propertyName} Hotel` : 'the hotel'} on your behalf to
          organise your booking change. Please note payment adjustments may apply
          <ChangeDatesRequestDisclaimer.Link />.
        </Paragraph>

        <ContentHeading>New stay dates</ContentHeading>
        <Card display="flex" p={[5, 6]} mb={[5, 8]}>
          <Row spacing="10px" flexWrap="wrap">
            <Column>
              <Field
                name="checkIn"
                label="Check-in date"
                title="Select check-in date"
                minDate={minCheckInDate}
                component={DatePickerInput}
              />
            </Column>
            <Column>
              <Field
                name="checkOut"
                label="Check-out date"
                title="Select check-out date"
                minDate={minCheckOutDate}
                maxDate={maxCheckOutDate}
                component={DatePickerInput}
              />
            </Column>
          </Row>
        </Card>

        <ContentHeading>How we’ll contact you</ContentHeading>
        <Text as="p" fontSize="lg" mb={5}>
          We’ll contact you within 24 hours to confirm your changes. Changing details here will not affect your booking.
        </Text>
        <Card display="flex" p={[5, 6]} mb={[5, 8]}>
          <Row spacing="10px" flexWrap="wrap">
            <Column>
              <Field name="phoneNumber" label="Phone number" component={PhoneInput} />
            </Column>
            <Column>
              <Field name="email" type="email" label="Email address" />
            </Column>
          </Row>
        </Card>
        <ButtonGroup
          desktopBackLabel="Cancel"
          mobileBackLabel="Cancel"
          onClickBack={onClickBack}
          continueLabel="Continue"
          onClickContinue={handleSubmit}
          continueButtonProps={{ type: 'submit', disabled: !isValid || !doDatesDifferFromOriginal }}
        />
      </TwoColumnWithBookingSummary>

      <Container mt={16} mb={[20, 16]}>
        <ChangeDatesRequestDisclaimer.Body />
      </Container>
    </Form>
  );
};

const Column = (props) => <Box width={[1, 1 / 2]} {...props} />;

export default RequestForm;
